import type { CSSProperties } from 'react';

/** Pure camera math for the zoomable universe. World units = the scene's SVG units. */
export interface Camera {
  /** World point shown at the viewport centre. */
  x: number;
  y: number;
  /** Screen pixels per world unit. */
  scale: number;
}
export interface Viewport { width: number; height: number }
export interface Bounds { minX: number; minY: number; maxX: number; maxY: number }
export type ZoomLevel = 'system' | 'planet';

/** Low enough that "Fit all" still fits a sky of ~80 planets on a phone. */
export const MIN_SCALE = 0.05;
export const MAX_SCALE = 40;
/** From here one planet is the subject: orbits freeze, stats appear. */
export const PLANET_SCALE = 2.2;

export function clampScale(s: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
}

export function zoomLevel(scale: number): ZoomLevel {
  return scale >= PLANET_SCALE ? 'planet' : 'system';
}

export function screenToWorld(cam: Camera, vp: Viewport, p: { x: number; y: number }): { x: number; y: number } {
  return { x: (p.x - vp.width / 2) / cam.scale + cam.x, y: (p.y - vp.height / 2) / cam.scale + cam.y };
}

/** Zoom by `factor`, keeping the world point under `screenPoint` where it is. */
export function zoomAt(cam: Camera, vp: Viewport, factor: number, screenPoint: { x: number; y: number }): Camera {
  const anchor = screenToWorld(cam, vp, screenPoint);
  const scale = clampScale(cam.scale * factor);
  return {
    scale,
    x: anchor.x - (screenPoint.x - vp.width / 2) / scale,
    y: anchor.y - (screenPoint.y - vp.height / 2) / scale,
  };
}

/** Drag by (dx, dy) screen pixels: the world follows the pointer. */
export function panBy(cam: Camera, dx: number, dy: number): Camera {
  return { ...cam, x: cam.x - dx / cam.scale, y: cam.y - dy / cam.scale };
}

export function fitBounds(b: Bounds, vp: Viewport, padding = 24): Camera {
  const w = Math.max(1, b.maxX - b.minX);
  const h = Math.max(1, b.maxY - b.minY);
  const scale = clampScale(Math.min((vp.width - 2 * padding) / w, (vp.height - 2 * padding) / h));
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2, scale };
}

/** World text that stays `px` tall on screen at any zoom (`--lk` = 1 / camera scale, set by usePanZoom). */
export function screenFont(px: number): CSSProperties {
  return { fontSize: `calc(${px}px * var(--lk, 1))` };
}

export function cameraTransform(cam: Camera, vp: Viewport): string {
  return `translate(${vp.width / 2} ${vp.height / 2}) scale(${cam.scale}) translate(${-cam.x} ${-cam.y})`;
}

/** k in [0, 1]: position linear, scale geometric (zooming feels even). */
export function lerpCamera(a: Camera, b: Camera, k: number): Camera {
  return {
    x: a.x + (b.x - a.x) * k,
    y: a.y + (b.y - a.y) * k,
    scale: a.scale * Math.pow(b.scale / a.scale, k),
  };
}
